<template>
  <page-layout
    class="property-page"
    :loaded="!!property"
    :title="property ? property.name : ''"
    :text="fullAddress"
    :media="propertyMedia"
    :socials="socials"
    place-text-bottom
  >
    <v-layout v-if="property" align-center class="fixed-nav" style="position: fixed; background-color: white; z-index: 1000; ">
      <v-container align-center class="d-flex">
        <h3 v-if="property">
          {{ property.name }}
        </h3>

        <div class="fixed-nav__actions">
          <div class="phone mr-5">
            <a class="text-secondary" :href="'tel:' + property.phone_number">{{ property.phone_number }}</a>
          </div>
          <v-btn color="primary" size="large" @click.native="checkAvailability"> Check Availability </v-btn>
        </div>
      </v-container>
    </v-layout>

    <PropertyLeasePeriods
      v-if="getBlockVisibility('lease-periods')"
      :property="property"
      :description="getBlockDescription('lease-periods')"
    />

    <property-friends
      v-if="getBlockVisibility('friends')"
      :property-id="`${property.id}`"
      :description="getBlockDescription('friends')"
    />

    <property-about v-if="getBlockVisibility('about')" :text="property.general_info" />

    <property-gallery
      v-if="getBlockVisibility('gallery') && property.property_images.length"
      :images="property.property_images"
      :description="getBlockDescription('gallery')"
    />
    <property-features
      v-if="getBlockVisibility('features') && property.amenities.length"
      :features="property.amenities"
      :description="getBlockDescription('features')"
    />
    <property-additional-resources
      v-if="getBlockVisibility('additional-resources') && property.additional_resources.length"
      :resources="property.additional_resources"
      :description="getBlockDescription('additional-resources')"
    />
    <property-location v-if="getBlockVisibility('location')" :address="fullAddress" />
    <testimonials-carousel v-if="getBlockVisibility('what-people-say')" :property-id="`${property.id}`" />
    <how-it-works v-if="getBlockVisibility('how-it-works')" />
    <contact-form v-if="getBlockVisibility('contact-form')" :property-id="property.id" :contact-data="property" />
  </page-layout>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from "vue-facing-decorator";

import PageLayout from "../components/common/PageLayout.vue";
import InstagramWidget from "../components/common/InstagramWidget.vue";
import TestimonialsCarousel from "../components/common/TestimonialsCarousel.vue";

import PropertyLeasePeriods from "../components/landing/LeasePeriods/PropertyLeasePeriods.vue";
import PropertyFriends from "../components/landing/PropertyFriends.vue";
import PropertyAbout from "../components/landing/PropertyAbout.vue";
import PropertyGallery from "../components/landing/PropertyGallery.vue";
import PropertyFeatures from "../components/landing/PropertyFeatures.vue";
import PropertyAdditionalResources from "../components/landing/PropertyAdditionalResources.vue";
import PropertyLocation from "../components/landing/PropertyLocation.vue";
import ContactForm from "../components/landing/ContactForm.vue";
import HowItWorks from "../components/landing/HowItWorks.vue";
import { authService } from "../services/authServiceInstance";

const namespace: string = "property";

@Component({
  components: {
    PageLayout,
    InstagramWidget,
    PropertyLeasePeriods,
    PropertyFriends,
    PropertyAbout,
    TestimonialsCarousel,
    PropertyGallery,
    PropertyFeatures,
    PropertyAdditionalResources,
    PropertyLocation,
    ContactForm,
    HowItWorks,
  },
})
export default class PropertyPage extends Vue {
  @Prop({ required: true, type: [String, Number] }) id!: string;

  @Watch("property")
  onPropertyLoad() {
    if (this.property.status === "DISABLED") {
      this.$router.push({ name: "Home" });
    }
    this.setTitle();
  }

  singleProperty(id: number | string) {
    return this.$store.getters[`${namespace}/singleProperty`](id);
  }

  get propertyLoading() {
    return this.$store.getters[`${namespace}/propertyLoading`];
  }

  get property() {
    return this.singleProperty(this.id);
  }

  get fullAddress() {
    if (!this.property || !this.property.code) return "";
    return `${this.property.street}, ${this.property.city}, ${this.property.state} ${this.property.code}`;
  }

  get propertyMedia() {
    return this.property
      ? this.property.background_media.sort((a, b) => a.order - b.order).map((media) => media.file)
      : [];
  }

  setTitle() {
    if (this.property) {
      document.title = "Room Choice | " + this.property.name;
    }
  }

  checkAvailability() {
    if (authService.hasRole("student")) return;
    this.$store.dispatch("ui/toggleAuthModal", {
      open: true,
      type: 'signup',
      data: {
        assigned_property: this.property.id,
      },
    });
  }

  async mounted() {
    if (!this.property || !this.property.full) {
      await this.$store.dispatch(`${namespace}/loadSingleProperty`, this.id);
    }
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
  }

  get socials() {
    return this.property
      ? [
          { type: "facebook", icon: "facebook", link: this.property.facebook },
          { type: "twitter", icon: "twitter", link: this.property.twitter },
          { type: "instagram", icon: "instagram", link: this.property.instagram },
        ].filter(({ link }) => link)
      : [];
  }

  getBlockVisibility(name: string) {
    if (!this.property) return false;
    const attr = this.property.website_block_attrs.find((attr) => attr.block_name === name);
    return attr ? attr.visibility : true;
  }

  getBlockDescription(name: string) {
    if (!this.property) return;
    const attr = this.property.website_block_attrs.find((attr) => attr.block_name === name);
    return attr ? attr.description : "";
  }
}
</script>

<style lang="scss">
.property-page {
  padding-bottom: 6rem;
}

.fixed-nav {
  position: fixed;
  height: 6rem;
  width: 100%;
  bottom: 0;
  left: 0;
  z-index: 10;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.25);
  background-color: var(--v-light-base);
  .v-container {
    flex-wrap: wrap;
    justify-content: space-between;
  }

  .fixed-nav__actions {
    display: flex;
    align-items: center;
  }

  @media (max-width: 680px) {
    .fixed-nav__actions {
      .phone {
        order: 2;
        margin-left: 1rem;
      }
    }

    h3 {
      width: 100%;
      font-size: 1.5rem;
      margin-bottom: 0.25rem;
    }
  }
}
</style>
